import { useMemo } from "react";
import type { QrCodeDoc } from "@/lib/types";
import { applyMenuOverride } from "@/lib/menuOverride";
import type { RestaurantInfo } from "./useRestaurant";

export interface ActiveMenuSettings {
  maxQuantity: number;
  maxOrders: number;
  waitingTime: number; // minutes
  maxQuantityPerMenu: number;
  allowedCategories: string[];
  menuCount: number;
  source: "menu" | "global";
}

/**
 * Mirrors the tablet logic in src/components/Restaurant.js:571-587, 727-736.
 * Priority: QRCode.order[].products[].menuSettings (the "initialOrder" carrying
 * a per-menu config) → fallback to Restaurant.buffetSettings. The table's own
 * settings (QRCode.menuOverride) then apply on top: see applyMenuOverride.
 */
export const useActiveMenuSettings = (
  qrCode: QrCodeDoc | null,
  restaurant: RestaurantInfo | null
): ActiveMenuSettings => {
  const buffetSettings = restaurant?.buffetSettings;
  return useMemo(
    () => applyMenuOverride(resolveMenuSettings(qrCode, buffetSettings), qrCode),
    [qrCode, buffetSettings]
  );
};

const resolveMenuSettings = (
  qrCode: QrCodeDoc | null,
  buffetSettings: RestaurantInfo["buffetSettings"]
): ActiveMenuSettings => {
  const orders = qrCode?.order;
  if (Array.isArray(orders)) {
    // Tablette : menuSettings cherché dans la commande `initialOrder`
    // (Restaurant.js:724-733). Les autres commandes ne servent que de repli
    // pour les tables où ce flag manque. Même ordre côté functions
    // (lib/menuSettings.ts).
    const candidates = [
      ...orders.filter((o) => o?.initialOrder),
      ...orders.filter((o) => !o?.initialOrder),
    ];
    for (const o of candidates) {
      const products = (o as { products?: Array<{ menuSettings?: Record<string, unknown>; quantity?: number }> })
        .products;
      if (!Array.isArray(products)) continue;
      const carrier = products.find((p) => p?.menuSettings);
      if (carrier?.menuSettings) {
        const ms = carrier.menuSettings as {
          maxQuantity?: number;
          maxOrders?: number;
          waitingTime?: number;
          maxQuantityPerMenu?: number;
          allowedCategories?: string[];
        };
        return {
          maxQuantity: ms.maxQuantity ?? 0,
          maxOrders: ms.maxOrders ?? 0,
          waitingTime: ms.waitingTime ?? 0,
          maxQuantityPerMenu: ms.maxQuantityPerMenu ?? 0,
          allowedCategories: ms.allowedCategories ?? [],
          menuCount: carrier.quantity ?? 1,
          source: "menu",
        };
      }
    }
  }
  const buffet = buffetSettings ?? {};
  return {
    maxQuantity: buffet.maxQuantity ?? 0,
    maxOrders: buffet.maxOrders ?? 0,
    waitingTime: buffet.waitingTime ?? 0,
    maxQuantityPerMenu: buffet.maxQuantityPerMenu ?? 0,
    allowedCategories: [],
    menuCount: buffet.menuCount ?? 1,
    source: "global",
  };
};
